import React, { useCallback, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import Flag from "react-flags";
import { Select, ListItem, makeStyles } from "@material-ui/core";
import { Typography } from "@totalsoft/rocket-webapp-components-deprecated";
import langSelectorStyle from "assets/jss/components/langSelectorStyle";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { LANGUAGES_QUERY } from "utils/languages";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { emptyArray } from "constants/defaultValues";
import { useApolloClient } from "@apollo/client";
import { defaultApolloCache } from "constants/defaultApolloCache";
import { AppContext } from "providers/AppContextProvider";
import { getTenant } from "providers/TenantProvider";
import { getFlagName } from "utils/functions";

const useStyles = makeStyles(langSelectorStyle);

function EmptyElement() {
  return <span></span>;
}

const LanguageSelector = ({ miniActive, className }) => {
  const classes = useStyles();
  const client = useApolloClient();
  const { setAppContext } = useContext(AppContext);
  const iconComponent = { IconComponent: EmptyElement };
  const { i18n } = useTranslation();
  const { countryCode } = getTenant();

  useEffect(() => setAppContext((prev) => ({ ...prev, language: i18n.language })), [setAppContext, i18n?.language]);

  const changeLanguage = useCallback(
    (lng) => {
      i18n.changeLanguage(lng.target.value);
      client.cache.reset();
      client.cache.restore(defaultApolloCache);
    },
    [i18n, client]
  );
  const containerClasses = classes.langSelectorContainer + " " + cx({ [className]: className != null });

  const { data, loading } = useQueryWithErrorHandling(LANGUAGES_QUERY, {
    onCompleted: (data) => {
      const languages = data?.languages || emptyArray;
      if (languages.length > 0 && !languages.some((lang) => lang.code === i18n.language))
        changeLanguage({ target: { value: languages[0].code } });
    },
  });
  const languages = data?.languages || emptyArray;

  return (
    <div className={containerClasses}>
      {!loading && languages.length > 0 && (
        <Select
          className={classes.langSelector}
          classes={{ selectMenu: classes.langSelectMenu }}
          value={i18n.language}
          onChange={changeLanguage}
          {...iconComponent}
        >
          {languages.map((language, index) => (
            <ListItem key={index} alignItems="center" value={language.code}>
              <Flag
                name={getFlagName(countryCode, language.culture.split("-")[1])}
                format="png"
                pngSize={32}
                shiny={true}
                basePath="/static/flags"
              />
              {!miniActive && <Typography className={classes.langSelectorText}>{language.shortName}</Typography>}
            </ListItem>
          ))}
        </Select>
      )}
    </div>
  );
};

LanguageSelector.propTypes = {
  miniActive: PropTypes.bool,
  className: PropTypes.string,
};

export default LanguageSelector;
